import { Settings, WalletIcon } from 'lucide-react'

import { ActionsGridLayout } from '../../types'
import { UseSettingsDialogResult } from '../logic/useSettingsDialog'
import { SlippageForm } from './slippage-form/SlippageForm'
import { MultiPanelDialog } from '@/features/dialogs/common/components/MultiPanelDialog'
import { Address } from '@/ui/atoms/address/Address'
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@/ui/atoms/dialog/Dialog'
import { IconButton } from '@/ui/atoms/icon-button/IconButton'
import { Switch } from '@/ui/atoms/switch/Switch'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/ui/atoms/tooltip/Tooltip'
import { testIds } from '@/ui/utils/testIds'

export interface SettingsDialogProps extends UseSettingsDialogResult {
  disabled?: boolean
  actionsGridLayout: ActionsGridLayout
  trigger?: React.ReactNode
}

export function SettingsDialog(props: SettingsDialogProps) {
  const defaultTrigger = (
    <IconButton
      icon={Settings}
      size="m"
      variant="transparent"
      data-testid={testIds.actions.settings.dialog}
      disabled={props.disabled}
    />
  )

  const trigger = props.trigger ?? defaultTrigger

  if (props.disabled) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent>Settings are disabled while actions are in progress.</TooltipContent>
      </Tooltip>
    )
  }

  return (
    <Dialog
      onOpenChange={(open) => {
        if (open) {
          // load initial values on open
          props.onOpen()
          return
        }
        // save if dialog is closed
        props.onConfirm()
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent overlayVariant={props.actionsGridLayout === 'compact' ? 'delicate' : 'default'}>
        <MultiPanelDialog>
          <DialogTitle>Settings</DialogTitle>
          <div className="flex items-center gap-8 rounded-sm bg-secondary p-6">
            <div className="flex flex-col gap-2">
              <h3 className="typography-label-2 flex items-center text-primary">Batch transactions when available</h3>
              <p className="typography-body-3 text-secondary">
                Transaction batching combines multiple actions into a single transaction, helping reduce gas fees and
                improve efficiency on the blockchain. Some wallets may prompt you to upgrade before using this feature.
              </p>
              <div className="mt-2 flex items-center gap-1.5">
                <span className="typography-label-3 text-fg-secondary">Applies to</span>
                <div className="typography-label-3 flex items-center gap-1 rounded-full bg-tertiary px-2 py-1 text-fg-secondary">
                  <WalletIcon className="icon-size-xxs text-fg-quaternary" />
                  <Address compact address={props.batchTransactionsSettings.accountAddress} />
                </div>
              </div>
            </div>
            <Switch
              className="ml-auto"
              checked={props.batchTransactionsSettings.preferTransactionsBatching}
              onClick={props.batchTransactionsSettings.togglePreferTransactionsBatching}
            />
          </div>
          <div className="flex items-center gap-8 rounded-sm bg-secondary p-6">
            <div className="flex flex-col gap-2">
              <h3 className="typography-label-2 text-primary">Use permits when available</h3>
              <p className="typography-body-3 text-secondary">
                Permits are a way to save gas by avoiding on-chain approve transactions. Instead signed permits are
                bundled with another transactions such as deposit or borrow.
              </p>
            </div>
            <Switch
              className="ml-auto"
              checked={props.permitsSettings.preferPermits}
              onClick={props.permitsSettings.togglePreferPermits}
              data-testid={testIds.actions.settings.preferPermitsSwitch}
            />
          </div>
          {import.meta.env.VITE_FEATURE_CONFIGURABLE_SLIPPAGE === '1' && (
            <div className="flex flex-col gap-8 rounded-sm bg-secondary p-6">
              <div className="flex flex-col gap-2">
                <h3 className="typography-label-2 text-primary">Slippage</h3>
                <p className="typography-body-3 text-secondary">
                  Your exchange transaction will revert if the price moves unfavorably by more than this percentage.
                  This applies only to savings transactions involving USDT.
                </p>
              </div>
              <SlippageForm {...props.slippageSettings} />
            </div>
          )}
        </MultiPanelDialog>
      </DialogContent>
    </Dialog>
  )
}
